import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { api } from "../api/client";
import { Card, KpiCard } from "../components/common/Card";
import { useAuth } from "../context/AuthContext";
import type { KpiSummary } from "../types";

interface SummaryCards {
  openWorkOrders: number;
  criticalOpen: number;
  pmDueSoon: number;
  lowStockCount: number;
  pendingRequests: number;
}
interface MonthlyTrendRow {
  month: string;
  wo_count: number;
  downtime_hours: number;
  total_cost: number;
}
interface TopDowntimeRow {
  asset_name: string;
  total_downtime_hours: number;
  failure_count: number;
}

export default function Dashboard() {
  const { hasPermission } = useAuth();
  const [cards, setCards] = useState<SummaryCards | null>(null);
  const [kpi, setKpi] = useState<KpiSummary | null>(null);
  const [trend, setTrend] = useState<MonthlyTrendRow[]>([]);
  const [topDowntime, setTopDowntime] = useState<TopDowntimeRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const summary = await api.get<SummaryCards>("/api/dashboard/summary-cards");
        setCards(summary);
        if (hasPermission("*", "kpi:view")) {
          const [k, t, d] = await Promise.all([
            api.get<KpiSummary>("/api/dashboard/kpi"),
            api.get<MonthlyTrendRow[]>("/api/dashboard/monthly-trend"),
            api.get<TopDowntimeRow[]>("/api/dashboard/top-downtime-machines?limit=10"),
          ]);
          setKpi(k);
          setTrend(t);
          setTopDowntime(d);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dashboard");
      }
    })();
  }, [hasPermission]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Maintenance Dashboard</h1>
        <p className="text-sm text-slate-500">Plant-wide overview of work orders, PM compliance, and inventory health.</p>
      </div>

      {error && <p className="rounded-md bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</p>}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard label="Open Work Orders" value={cards?.openWorkOrders ?? "…"} accent="blue" />
        <KpiCard label="Critical Open" value={cards?.criticalOpen ?? "…"} accent="red" />
        <KpiCard label="PM Due (7d)" value={cards?.pmDueSoon ?? "…"} accent="amber" />
        <KpiCard label="Low Stock Parts" value={cards?.lowStockCount ?? "…"} accent="amber" />
        <KpiCard label="Pending Requests" value={cards?.pendingRequests ?? "…"} accent="blue" />
      </div>

      {kpi && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard label="MTBF" value={`${kpi.mtbfHours} h`} accent="green" />
            <KpiCard label="MTTR" value={`${kpi.mttrHours} h`} accent="amber" />
            <KpiCard label="Machine Availability" value={`${kpi.machineAvailabilityPercent}%`} accent="green" />
            <KpiCard label="PM Compliance" value={`${kpi.pmCompliancePercent}%`} accent="blue" />
            <KpiCard label="Breakdown Frequency" value={kpi.breakdownFrequency} accent="red" />
            <KpiCard label="Total Downtime" value={`${kpi.totalDowntimeHours} h`} accent="red" />
            <KpiCard label="Maintenance Cost" value={`฿${kpi.maintenanceCost.toLocaleString()}`} accent="blue" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Monthly Downtime & Cost Trend">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="downtime_hours" name="Downtime (h)" stroke="#dc2626" strokeWidth={2} />
                  <Line type="monotone" dataKey="wo_count" name="Work Orders" stroke="#0ea5e9" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Top 10 Downtime Machines">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topDowntime} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis type="number" fontSize={12} />
                  <YAxis type="category" dataKey="asset_name" fontSize={11} width={120} />
                  <Tooltip />
                  <Bar dataKey="total_downtime_hours" name="Downtime (h)" fill="#f97316" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
